import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { Box } from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core';
import { Card, CardHeader, CardContent } from '@redshelf/redshelf-ui';
import NavigateToLocation from 'components/NavigateToLocation';
import IconTooltip from 'components/IconTooltip';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      fontFamily: theme.typography.fontFamily,
      borderRadius: 4,
      '&:focus-visible': {
        outline: 'none',
      },
    },

    snippetContainer: {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(2),
    },

    snippet: {
      lineHeight: theme.typography.pxToRem(22),
      letterSpacing: theme.typography.pxToRem(0.15),
      wordBreak: 'break-word',
    },

    disabledSnippetWrapper: {
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.shelfBlue[800]}`,
        outlineOffset: theme.spacing(-0.125),
      },
    },
  };
});

const OFFLINE_LINK_MESSAGE =
  'The link to this content is unavailable while offline';

const StyledCardHeader = withStyles((theme) => ({
  root: {
    fontFamily: theme.typography.fontFamily,
    margin: theme.spacing(0.5, 0.5, 1, 0),
    padding: 'unset',
    minWidth: theme.spacing(27.5),
  },
  action: {
    margin: 'unset',
  },
  avatar: {
    margin: theme.spacing(0.25, 1, 0, 0),
    padding: theme.spacing(0.25),
  },
  content: {
    marginTop: theme.spacing(0.5),
  },
  title: {
    ...theme.typography.caption,
    letterSpacing: theme.typography.pxToRem(0.4),
    color: theme.palette.grey[700],
  },
  subheader: {
    ...theme.typography.caption,
    letterSpacing: theme.typography.pxToRem(0.4),
    color: theme.palette.grey[700],
  },
}))(CardHeader);

const StyledCardContent = withStyles((theme) => ({
  root: {
    ...theme.typography.body2,
    color: theme.palette.greyBlue[700],
    fontFamily: theme.typography.fontFamily,
    padding: theme.spacing(0),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(2),

    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
}))(CardContent);

const StyledNavigateToLocation = withStyles((theme) => ({
  root: {
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
      textDecoration: 'none',
      color: 'inherit',
    },
  },
}))(NavigateToLocation);

const UserContentCard = (
  {
    action,
    avatar,
    bookLocation,
    classNames,
    children,
    id,
    linkLabel,
    lozenge,
    snippet,
    subheader,
    tabIndex,
    title,
    isSnippetLinkAvailable = true,
    onSnippetClick = () => {},
    ...props
  },
  ref,
) => {
  const classes = useStyles();

  const snippetTextJSX = (
    <span
      className={clsx(classes.snippet, classNames?.highlight, 'noTranslate')}
    >
      {snippet}
    </span>
  );

  const snippetJSX = isSnippetLinkAvailable ? (
    <StyledNavigateToLocation
      bookLocation={bookLocation}
      linkLabel={linkLabel}
      onClick={onSnippetClick}
      id={id}
    >
      {snippetTextJSX}
    </StyledNavigateToLocation>
  ) : (
    <IconTooltip
      id="user-card-offline"
      placement="top"
      title={OFFLINE_LINK_MESSAGE}
      showTooltipOnMobileInteraction={true}
    >
      <p tabIndex="0" className={classes.disabledSnippetWrapper}>
        {snippetTextJSX}
      </p>
    </IconTooltip>
  );

  return (
    <Card
      id={id}
      className={clsx(classes.root, classNames?.root)}
      ref={ref}
      tabIndex={tabIndex ?? null}
      {...props}
    >
      <StyledCardHeader
        action={action}
        avatar={avatar}
        title={title}
        subheader={subheader}
      />
      <StyledCardContent className={classes.cardContent}>
        {lozenge && lozenge}
        {snippet && (
          <Box className={classes.snippetContainer}>{snippetJSX}</Box>
        )}
        {children}
      </StyledCardContent>
    </Card>
  );
};

export default forwardRef(UserContentCard);
